import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ShoppingBag, ShieldCheck, Truck, CreditCard, ClipboardCheck, ArrowRight, ArrowLeft, Info, X, Star, User, Tag, Lock } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useStore } from '../context/StoreContext';
import { YARBOCARE_PLANS, YARBOCARE_DISCLAIMER, YARBOCARE_PROVIDER_NOTE, isEligibleForYarboCare, getPlanById, YarboCarePlanId } from '../data/yarboCare';
export function Checkout() {
  const navigate = useNavigate();
  const {
    items,
    itemCount,
    productSubtotal,
    protectionSubtotal,
    subtotal,
    total,
    discountAmount,
    shippingEstimate,
    taxEstimate,
    discountCode,
    shippingMethod,
    attachProtection,
    removeProtection,
    applyDiscount,
    removeDiscount,
    setShippingMethod,
    clearCart
  } = useCart();
  const {
    addOrder,
    addCustomer,
    login,
    currentUser,
    validateDiscount
  } = useStore();
  const eligibleItems = useMemo(() => items.filter(i => isEligibleForYarboCare(i.product) || i.product.eligibleForYarboCare), [items]);
  const hasProtectionStep = eligibleItems.length > 0;
  const allSteps = useMemo(() => {
    const s: {
      key: string;
      label: string;
      icon: any;
    }[] = [{
      key: 'customer',
      label: 'Customer',
      icon: User
    }];
    if (hasProtectionStep) {
      s.push({
        key: 'protection',
        label: 'Protection',
        icon: ShieldCheck
      });
    }
    s.push({
      key: 'shipping',
      label: 'Shipping',
      icon: Truck
    });
    s.push({
      key: 'discount',
      label: 'Discount',
      icon: Tag
    });
    s.push({
      key: 'payment',
      label: 'Payment',
      icon: CreditCard
    });
    s.push({
      key: 'review',
      label: 'Review',
      icon: ClipboardCheck
    });
    return s;
  }, [hasProtectionStep]);
  const [stepIdx, setStepIdx] = useState(0);
  const currentStep = allSteps[Math.min(stepIdx, allSteps.length - 1)];
  // Form State
  const [email, setEmail] = useState(currentUser?.email || '');
  const [firstName, setFirstName] = useState(currentUser?.firstName || '');
  const [lastName, setLastName] = useState(currentUser?.lastName || '');
  const [phone, setPhone] = useState(currentUser?.phone || '');
  const [createAccount, setCreateAccount] = useState(false);
  const [shippingAddress, setShippingAddress] = useState({
    line1: '',
    line2: '',
    city: '',
    state: '',
    zip: '',
    country: 'USA'
  });
  const [billingSameAsShipping, setBillingSameAsShipping] = useState(true);
  const [billingAddress, setBillingAddress] = useState({
    line1: '',
    line2: '',
    city: '',
    state: '',
    zip: '',
    country: 'USA'
  });
  const [paymentMethod, setPaymentMethod] = useState<'Card' | 'ApplePay' | 'GooglePay'>('Card');
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvc, setCardCvc] = useState('');
  const [cardZip, setCardZip] = useState('');
  const [discountInput, setDiscountInput] = useState('');
  const [discountError, setDiscountError] = useState('');
  useEffect(() => {
    if (currentUser) {
      setEmail(currentUser.email);
      setFirstName(currentUser.firstName);
      setLastName(currentUser.lastName);
      setPhone(currentUser.phone);
      if (currentUser.addresses.length > 0) {
        setShippingAddress(currentUser.addresses[0]);
      }
    }
  }, [currentUser]);
  if (items.length === 0) {
    return <div className="bg-gray-50 min-h-[60vh] flex items-center" data-id="element-1101">
        <div className="max-w-md mx-auto text-center px-4 py-16" data-id="element-1102">
          <ShoppingBag className="w-16 h-16 text-gray-300 mx-auto mb-6" data-id="element-1103" />
          <h1 className="text-3xl font-extrabold text-yarbo-dark mb-3" data-id="element-1104">
            Your cart is empty
          </h1>
          <Link to="/shop" className="inline-block bg-yarbo-dark text-white font-bold px-8 py-4 rounded-lg hover:bg-black transition-colors" data-id="element-1105">
            Shop All Products
          </Link>
        </div>
      </div>;
  }
  const goNext = () => setStepIdx(i => Math.min(i + 1, allSteps.length - 1));
  const goBack = () => {
    if (stepIdx === 0) navigate('/cart');else setStepIdx(i => Math.max(0, i - 1));
  };
  const handleApplyDiscount = () => {
    const res = validateDiscount(discountInput, subtotal);
    if (res.valid) {
      applyDiscount(discountInput, res.amount);
      setDiscountError('');
      setDiscountInput('');
    } else {
      setDiscountError(res.error || 'Invalid code');
    }
  };
  const handlePlaceOrder = () => {
    const orderId = `MR-${Math.floor(10000 + Math.random() * 90000)}`;
    if (createAccount && !currentUser) {
      addCustomer({
        id: `CUST-${Math.floor(Math.random() * 10000)}`,
        firstName,
        lastName,
        email,
        phone,
        addresses: [shippingAddress]
      });
      login(email);
    }
    const orderItems = items.map(item => {
      const plan = getPlanById(item.protectionPlanId);
      return {
        productName: item.product.name,
        sku: item.product.sku,
        qty: item.quantity,
        unitPrice: item.product.price,
        protection: plan ? {
          plan: plan.title,
          price: plan.priceMockup
        } : undefined
      };
    });
    let brand = 'Visa';
    if (cardNumber.startsWith('5')) brand = 'Mastercard';
    if (cardNumber.startsWith('3')) brand = 'Amex';
    if (cardNumber.startsWith('6')) brand = 'Discover';
    addOrder({
      id: orderId,
      placedAt: new Date().toISOString(),
      status: 'paid',
      customer: {
        firstName,
        lastName,
        email,
        phone
      },
      shipping: shippingAddress,
      billing: billingSameAsShipping ? shippingAddress : billingAddress,
      payment: {
        method: paymentMethod,
        last4: cardNumber.slice(-4) || '1234'
      },
      items: orderItems,
      shippingCost: shippingEstimate || 0,
      tax: taxEstimate || 0,
      notes: discountCode ? `Discount applied: ${discountCode}` : undefined,
      discountCode: discountCode || undefined,
      discountAmount: discountAmount || undefined,
      subtotalBeforeDiscount: subtotal,
      subtotalAfterDiscount: subtotal - discountAmount
    });
    clearCart();
    navigate(`/checkout/confirmation?orderId=${orderId}`);
  };
  return <div className="bg-gray-50 min-h-screen" data-id="element-1106">
      {/* Stepper */}
      <div className="bg-white border-b border-gray-200" data-id="element-1107">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-id="element-1108">
          <h1 className="text-2xl md:text-3xl font-extrabold text-yarbo-dark mb-4" data-id="element-1109">
            Checkout
          </h1>
          <ol className="flex items-center gap-1 sm:gap-2 overflow-x-auto hide-scrollbar" data-id="element-1110">
            {allSteps.map((s, i) => {
            const done = i < stepIdx;
            const active = i === stepIdx;
            return <li key={s.key} className="flex items-center flex-shrink-0" data-id="element-1111">
                  <button onClick={() => i <= stepIdx && setStepIdx(i)} disabled={i > stepIdx} className={`flex items-center gap-2 px-2.5 sm:px-3.5 py-2 rounded-lg text-xs sm:text-sm font-bold transition-colors ${active ? 'bg-yarbo-dark text-white' : done ? 'bg-yarbo-yellow/30 text-yarbo-dark' : 'text-gray-400'}`} data-id="element-1112">
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${active ? 'bg-yarbo-yellow text-yarbo-dark' : done ? 'bg-yarbo-dark text-yarbo-yellow' : 'bg-gray-200 text-gray-500'}`} data-id="element-1113">
                      {done ? <Check className="w-3 h-3" data-id="element-1114" /> : i + 1}
                    </span>
                    <span className="hidden sm:inline" data-id="element-1115">{s.label}</span>
                  </button>
                  {i < allSteps.length - 1 && <span className="w-4 sm:w-6 h-px bg-gray-200 mx-0.5 sm:mx-1" data-id="element-1116" />}
                </li>;
          })}
          </ol>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-1117">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1118">
          {/* Step content */}
          <div className="lg:col-span-2" data-id="element-1119">
            <AnimatePresence mode="wait" data-id="element-1120">
              <motion.div key={currentStep.key} initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -8
            }} transition={{
              duration: 0.2
            }} className="bg-white rounded-2xl border border-gray-200 p-6 sm:p-8" data-id="element-1121">
                {currentStep.key === 'customer' && <div className="space-y-4" data-id="element-1122">
                    <h2 className="text-xl font-bold text-yarbo-dark mb-4" data-id="element-1123">
                      Customer Information
                    </h2>
                    <div className="grid grid-cols-2 gap-4" data-id="element-1124">
                      <div data-id="element-1125">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1126">
                          First Name
                        </label>
                        <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} className="w-full border-gray-300 rounded-lg shadow-sm focus:border-yarbo-dark focus:ring-yarbo-dark" data-id="element-1127" />
                      </div>
                      <div data-id="element-1128">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1129">
                          Last Name
                        </label>
                        <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} className="w-full border-gray-300 rounded-lg shadow-sm focus:border-yarbo-dark focus:ring-yarbo-dark" data-id="element-1130" />
                      </div>
                    </div>
                    <div data-id="element-1131">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1132">
                        Email Address
                      </label>
                      <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full border-gray-300 rounded-lg shadow-sm focus:border-yarbo-dark focus:ring-yarbo-dark" data-id="element-1133" />
                    </div>
                    <div data-id="element-1134">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1135">
                        Phone Number
                      </label>
                      <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} className="w-full border-gray-300 rounded-lg shadow-sm focus:border-yarbo-dark focus:ring-yarbo-dark" data-id="element-1136" />
                    </div>
                    {!currentUser && <div className="flex items-center mt-4" data-id="element-1137">
                        <input id="create-account" type="checkbox" checked={createAccount} onChange={e => setCreateAccount(e.target.checked)} className="h-4 w-4 text-yarbo-dark focus:ring-yarbo-dark border-gray-300 rounded" data-id="element-1138" />
                        <label htmlFor="create-account" className="ml-2 block text-sm text-gray-900" data-id="element-1139">
                          Create an account for faster checkout next time
                        </label>
                      </div>}
                  </div>}

                {currentStep.key === 'protection' && <ProtectionStep items={eligibleItems} attachProtection={attachProtection} removeProtection={removeProtection} data-id="element-1140" />}

                {currentStep.key === 'shipping' && <div className="space-y-6" data-id="element-1141">
                    <div className="space-y-4" data-id="element-1142">
                      <h2 className="text-xl font-bold text-yarbo-dark mb-4" data-id="element-1143">
                        Shipping Address
                      </h2>
                      <div data-id="element-1144">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1145">
                          Address Line 1
                        </label>
                        <input type="text" value={shippingAddress.line1} onChange={e => setShippingAddress({
                      ...shippingAddress,
                      line1: e.target.value
                    })} className="w-full border-gray-300 rounded-lg shadow-sm focus:border-yarbo-dark focus:ring-yarbo-dark" data-id="element-1146" />
                      </div>
                      <div data-id="element-1147">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1148">
                          Address Line 2 (Optional)
                        </label>
                        <input type="text" value={shippingAddress.line2} onChange={e => setShippingAddress({
                      ...shippingAddress,
                      line2: e.target.value
                    })} className="w-full border-gray-300 rounded-lg shadow-sm focus:border-yarbo-dark focus:ring-yarbo-dark" data-id="element-1149" />
                      </div>
                      <div className="grid grid-cols-6 gap-4" data-id="element-1150">
                        <div className="col-span-3" data-id="element-1151">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1152">
                            City
                          </label>
                          <input type="text" value={shippingAddress.city} onChange={e => setShippingAddress({
                        ...shippingAddress,
                        city: e.target.value
                      })} className="w-full border-gray-300 rounded-lg shadow-sm focus:border-yarbo-dark focus:ring-yarbo-dark" data-id="element-1153" />
                        </div>
                        <div className="col-span-1" data-id="element-1154">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1155">
                            State
                          </label>
                          <input type="text" value={shippingAddress.state} onChange={e => setShippingAddress({
                        ...shippingAddress,
                        state: e.target.value
                      })} className="w-full border-gray-300 rounded-lg shadow-sm focus:border-yarbo-dark focus:ring-yarbo-dark" data-id="element-1156" />
                        </div>
                        <div className="col-span-2" data-id="element-1157">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1158">
                            ZIP Code
                          </label>
                          <input type="text" value={shippingAddress.zip} onChange={e => setShippingAddress({
                        ...shippingAddress,
                        zip: e.target.value
                      })} className="w-full border-gray-300 rounded-lg shadow-sm focus:border-yarbo-dark focus:ring-yarbo-dark" data-id="element-1159" />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4 pt-6 border-t border-gray-200" data-id="element-1160">
                      <h2 className="text-xl font-bold text-yarbo-dark mb-4" data-id="element-1161">
                        Shipping Method
                      </h2>
                      <div className="space-y-3" data-id="element-1162">
                        <label className={`flex items-center justify-between p-4 border rounded-xl cursor-pointer transition-colors ${shippingMethod === 'standard' ? 'border-yarbo-dark bg-yarbo-yellow/5' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-1163">
                          <div className="flex items-center gap-3" data-id="element-1164">
                            <input type="radio" name="shippingMethod" checked={shippingMethod === 'standard'} onChange={() => setShippingMethod('standard')} className="h-4 w-4 text-yarbo-dark focus:ring-yarbo-dark border-gray-300" data-id="element-1165" />
                            <div data-id="element-1166">
                              <div className="font-bold text-gray-900" data-id="element-1167">
                                Standard Freight Shipping
                              </div>
                              <div className="text-sm text-gray-500" data-id="element-1168">
                                5-7 business days
                              </div>
                            </div>
                          </div>
                          <div className="font-medium text-gray-900" data-id="element-1169">
                            Calculated at checkout
                          </div>
                        </label>
                        <label className={`flex items-center justify-between p-4 border rounded-xl cursor-pointer transition-colors ${shippingMethod === 'expedited' ? 'border-yarbo-dark bg-yarbo-yellow/5' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-1170">
                          <div className="flex items-center gap-3" data-id="element-1171">
                            <input type="radio" name="shippingMethod" checked={shippingMethod === 'expedited'} onChange={() => setShippingMethod('expedited')} className="h-4 w-4 text-yarbo-dark focus:ring-yarbo-dark border-gray-300" data-id="element-1172" />
                            <div data-id="element-1173">
                              <div className="font-bold text-gray-900" data-id="element-1174">
                                Expedited Freight Shipping
                              </div>
                              <div className="text-sm text-gray-500" data-id="element-1175">
                                2-3 business days
                              </div>
                            </div>
                          </div>
                          <div className="font-medium text-gray-900" data-id="element-1176">
                            $249.00
                          </div>
                        </label>
                        <label className={`flex items-center justify-between p-4 border rounded-xl cursor-pointer transition-colors ${shippingMethod === 'pickup' ? 'border-yarbo-dark bg-yarbo-yellow/5' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-1177">
                          <div className="flex items-center gap-3" data-id="element-1178">
                            <input type="radio" name="shippingMethod" checked={shippingMethod === 'pickup'} onChange={() => setShippingMethod('pickup')} className="h-4 w-4 text-yarbo-dark focus:ring-yarbo-dark border-gray-300" data-id="element-1179" />
                            <div data-id="element-1180">
                              <div className="font-bold text-gray-900" data-id="element-1181">
                                Local Pickup / Arrange Pickup
                              </div>
                              <div className="text-sm text-gray-500" data-id="element-1182">
                                Michigan facility
                              </div>
                            </div>
                          </div>
                          <div className="font-medium text-gray-900" data-id="element-1183">$0.00</div>
                        </label>
                      </div>
                    </div>
                  </div>}

                {currentStep.key === 'discount' && <div className="space-y-4" data-id="element-1184">
                    <h2 className="text-xl font-bold text-yarbo-dark mb-4" data-id="element-1185">
                      Discount Code
                    </h2>
                    {discountCode ? <div className="bg-green-50 border border-green-200 p-4 rounded-lg flex items-center justify-between" data-id="element-1186">
                        <div data-id="element-1187">
                          <p className="text-green-800 font-bold" data-id="element-1188">
                            Code Applied: {discountCode}
                          </p>
                          <p className="text-green-600 text-sm" data-id="element-1189">
                            You saved ${discountAmount.toFixed(2)}
                          </p>
                        </div>
                        <button onClick={removeDiscount} className="text-green-800 hover:text-green-900" data-id="element-1190">
                          <X className="w-5 h-5" data-id="element-1191" />
                        </button>
                      </div> : <div className="flex gap-2" data-id="element-1192">
                        <input type="text" value={discountInput} onChange={e => setDiscountInput(e.target.value)} placeholder="Enter code (e.g. YARBO10)" className="flex-1 border-gray-300 rounded-lg shadow-sm focus:border-yarbo-dark focus:ring-yarbo-dark" data-id="element-1193" />
                        <button onClick={handleApplyDiscount} className="bg-gray-900 text-white px-4 py-2 rounded-lg font-bold hover:bg-black" data-id="element-1194">
                          Apply
                        </button>
                      </div>}
                    {discountError && <p className="text-red-600 text-sm mt-1" data-id="element-1195">
                        {discountError}
                      </p>}
                  </div>}

                {currentStep.key === 'payment' && <div className="space-y-6" data-id="element-1196">
                    <div className="flex items-center justify-between mb-4" data-id="element-1197">
                      <h2 className="text-xl font-bold text-yarbo-dark" data-id="element-1198">
                        Payment
                      </h2>
                      <div className="flex items-center gap-1.5 text-xs font-bold text-green-700 bg-green-50 px-2.5 py-1 rounded-full" data-id="element-1199">
                        <Lock className="w-3 h-3" data-id="element-1200" /> Secure Checkout
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-3" data-id="element-1201">
                      {['Card', 'ApplePay', 'GooglePay'].map(method => <button key={method} onClick={() => setPaymentMethod(method as any)} className={`p-3 border rounded-lg font-bold text-sm ${paymentMethod === method ? 'border-yarbo-dark bg-yarbo-yellow/10 text-yarbo-dark' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`} data-id="element-1202">
                          {method === 'Card' ? 'Credit Card' : method === 'ApplePay' ? 'Apple Pay' : 'Google Pay'}
                        </button>)}
                    </div>

                    {paymentMethod === 'Card' && <div className="space-y-4 bg-gray-50 p-5 rounded-xl border border-gray-200" data-id="element-1203">
                        <div data-id="element-1204">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1205">
                            Card Number
                          </label>
                          <div className="relative" data-id="element-1206">
                            <input type="text" value={cardNumber} onChange={e => setCardNumber(e.target.value)} placeholder="0000 0000 0000 0000" className="w-full border-gray-300 rounded-lg shadow-sm focus:border-yarbo-dark focus:ring-yarbo-dark pl-10" data-id="element-1207" />
                            <CreditCard className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-1208" />
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-4" data-id="element-1209">
                          <div className="col-span-1" data-id="element-1210">
                            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1211">
                              Expiration
                            </label>
                            <input type="text" value={cardExpiry} onChange={e => setCardExpiry(e.target.value)} placeholder="MM/YY" className="w-full border-gray-300 rounded-lg shadow-sm focus:border-yarbo-dark focus:ring-yarbo-dark" data-id="element-1212" />
                          </div>
                          <div className="col-span-1" data-id="element-1213">
                            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1214">
                              CVC
                            </label>
                            <input type="text" value={cardCvc} onChange={e => setCardCvc(e.target.value)} placeholder="123" className="w-full border-gray-300 rounded-lg shadow-sm focus:border-yarbo-dark focus:ring-yarbo-dark" data-id="element-1215" />
                          </div>
                          <div className="col-span-1" data-id="element-1216">
                            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1217">
                              ZIP
                            </label>
                            <input type="text" value={cardZip} onChange={e => setCardZip(e.target.value)} placeholder="12345" className="w-full border-gray-300 rounded-lg shadow-sm focus:border-yarbo-dark focus:ring-yarbo-dark" data-id="element-1218" />
                          </div>
                        </div>
                      </div>}

                    <div className="pt-4 border-t border-gray-200" data-id="element-1219">
                      <div className="flex items-center mb-4" data-id="element-1220">
                        <input id="same-billing" type="checkbox" checked={billingSameAsShipping} onChange={e => setBillingSameAsShipping(e.target.checked)} className="h-4 w-4 text-yarbo-dark focus:ring-yarbo-dark border-gray-300 rounded" data-id="element-1221" />
                        <label htmlFor="same-billing" className="ml-2 block text-sm text-gray-900" data-id="element-1222">
                          Billing address is same as shipping
                        </label>
                      </div>

                      {!billingSameAsShipping && <div className="space-y-4" data-id="element-1223">
                          <h3 className="text-lg font-bold text-yarbo-dark" data-id="element-1224">
                            Billing Address
                          </h3>
                          <div data-id="element-1225">
                            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1226">
                              Address Line 1
                            </label>
                            <input type="text" value={billingAddress.line1} onChange={e => setBillingAddress({
                        ...billingAddress,
                        line1: e.target.value
                      })} className="w-full border-gray-300 rounded-lg shadow-sm focus:border-yarbo-dark focus:ring-yarbo-dark" data-id="element-1227" />
                          </div>
                          <div className="grid grid-cols-6 gap-4" data-id="element-1228">
                            <div className="col-span-3" data-id="element-1229">
                              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1230">
                                City
                              </label>
                              <input type="text" value={billingAddress.city} onChange={e => setBillingAddress({
                          ...billingAddress,
                          city: e.target.value
                        })} className="w-full border-gray-300 rounded-lg shadow-sm focus:border-yarbo-dark focus:ring-yarbo-dark" data-id="element-1231" />
                            </div>
                            <div className="col-span-1" data-id="element-1232">
                              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1233">
                                State
                              </label>
                              <input type="text" value={billingAddress.state} onChange={e => setBillingAddress({
                          ...billingAddress,
                          state: e.target.value
                        })} className="w-full border-gray-300 rounded-lg shadow-sm focus:border-yarbo-dark focus:ring-yarbo-dark" data-id="element-1234" />
                            </div>
                            <div className="col-span-2" data-id="element-1235">
                              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1236">
                                ZIP
                              </label>
                              <input type="text" value={billingAddress.zip} onChange={e => setBillingAddress({
                          ...billingAddress,
                          zip: e.target.value
                        })} className="w-full border-gray-300 rounded-lg shadow-sm focus:border-yarbo-dark focus:ring-yarbo-dark" data-id="element-1237" />
                            </div>
                          </div>
                        </div>}
                    </div>
                  </div>}

                {currentStep.key === 'review' && <div className="space-y-6" data-id="element-1238">
                    <h2 className="text-xl font-bold text-yarbo-dark mb-4" data-id="element-1239">
                      Review Your Order
                    </h2>

                    <div className="bg-gray-50 p-4 rounded-xl border border-gray-200" data-id="element-1240">
                      <h3 className="font-bold text-gray-900 mb-2" data-id="element-1241">
                        Shipping To
                      </h3>
                      <p className="text-sm text-gray-600" data-id="element-1242">
                        {firstName} {lastName}
                        <br data-id="element-1243" />
                        {shippingAddress.line1} {shippingAddress.line2}
                        <br data-id="element-1244" />
                        {shippingAddress.city}, {shippingAddress.state}{' '}
                        {shippingAddress.zip}
                      </p>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-xl border border-gray-200" data-id="element-1245">
                      <h3 className="font-bold text-gray-900 mb-2" data-id="element-1246">
                        Payment Method
                      </h3>
                      <p className="text-sm text-gray-600" data-id="element-1247">
                        {paymentMethod} ending in{' '}
                        {cardNumber.slice(-4) || '1234'}
                      </p>
                    </div>
                  </div>}
              </motion.div>
            </AnimatePresence>

            <div className="mt-6 flex items-center justify-between" data-id="element-1248">
              <button onClick={goBack} className="inline-flex items-center text-sm font-bold text-gray-700 hover:text-yarbo-dark" data-id="element-1249">
                <ArrowLeft className="w-4 h-4 mr-1.5" data-id="element-1250" />
                {stepIdx === 0 ? 'Back to Cart' : 'Previous Step'}
              </button>
              {currentStep.key !== 'review' ? <button onClick={goNext} className="inline-flex items-center bg-yarbo-dark text-white font-bold px-6 py-3 rounded-lg hover:bg-black transition-colors" data-id="element-1251">
                  {currentStep.key === 'protection' ? eligibleItems.some(i => i.protectionPlanId) ? 'Add Protection & Continue' : 'Continue Without Protection' : 'Continue'}{' '}
                  <ArrowRight className="ml-1.5 w-4 h-4" data-id="element-1252" />
                </button> : <button onClick={handlePlaceOrder} className="inline-flex items-center bg-yarbo-yellow text-yarbo-dark font-bold px-8 py-4 rounded-lg hover:brightness-95 transition-all text-lg shadow-lg" data-id="element-1253">
                  Place Order
                </button>}
            </div>
          </div>

          {/* Order summary */}
          <aside className="lg:col-span-1" data-id="element-1254">
            <OrderSummary items={items} itemCount={itemCount} productSubtotal={productSubtotal} protectionSubtotal={protectionSubtotal} subtotal={subtotal} discountAmount={discountAmount} shippingEstimate={shippingEstimate} taxEstimate={taxEstimate} total={total} removeProtection={removeProtection} data-id="element-1255" />
          </aside>
        </div>
      </div>
    </div>;
}
function ProtectionStep({
  items,
  attachProtection,
  removeProtection
}: {
  items: ReturnType<typeof useCart>['items'];
  attachProtection: (id: string, planId: YarboCarePlanId) => void;
  removeProtection: (id: string) => void;
}) {
  return <div data-id="element-1256">
      <div className="flex items-start gap-3 mb-2" data-id="element-1257">
        <div className="w-10 h-10 rounded-full bg-yarbo-yellow flex items-center justify-center flex-shrink-0" data-id="element-1258">
          <ShieldCheck className="w-5 h-5 text-yarbo-dark" data-id="element-1259" />
        </div>
        <div data-id="element-1260">
          <h2 className="text-xl font-extrabold text-yarbo-dark leading-tight" data-id="element-1261">
            Add YarboCare Before Checkout
          </h2>
          <p className="text-sm text-gray-600 mt-1" data-id="element-1262">
            Choose optional protection for your eligible Yarbo system, or
            continue without added coverage.
          </p>
        </div>
      </div>

      <div className="mt-6 space-y-6" data-id="element-1263">
        {items.map(({
        product,
        protectionPlanId
      }) => <div key={product.id} className="border border-gray-200 rounded-xl overflow-hidden" data-id="element-1264">
            <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 border-b border-gray-200" data-id="element-1265">
              <div className="w-12 h-12 bg-white rounded-lg p-1.5 border border-gray-200" data-id="element-1266">
                <img src={product.image} alt={product.name} className="w-full h-full object-contain" data-id="element-1267" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-1268">
                <div className="text-sm font-bold text-yarbo-dark leading-tight line-clamp-2" data-id="element-1269">
                  {product.name}
                </div>
                <div className="text-xs text-gray-500" data-id="element-1270">
                  ${product.price.toLocaleString()}
                </div>
              </div>
            </div>

            <div className="p-4 space-y-3" data-id="element-1271">
              {YARBOCARE_PLANS.map(plan => {
            const isSelected = protectionPlanId === plan.id;
            return <button key={plan.id} onClick={() => attachProtection(product.id, plan.id)} className={`w-full text-left p-4 rounded-xl border-2 transition-all ${isSelected ? 'border-yarbo-dark bg-yarbo-yellow/5' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-1272">
                    <div className="flex justify-between items-start mb-2" data-id="element-1273">
                      <div data-id="element-1274">
                        {plan.highlight && <span className="inline-block bg-yarbo-yellow text-yarbo-dark text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide mb-1" data-id="element-1275">
                            {plan.badge}
                          </span>}
                        <div className="font-bold text-yarbo-dark text-sm sm:text-base pr-4" data-id="element-1276">
                          {plan.title}
                        </div>
                      </div>
                      <div className="font-extrabold text-yarbo-dark whitespace-nowrap" data-id="element-1277">
                        ${plan.priceMockup}
                      </div>
                    </div>
                    <ul className="space-y-1 mt-3" data-id="element-1278">
                      {plan.bullets.map((b, i) => <li key={i} className="flex items-start text-xs text-gray-600" data-id="element-1279">
                          <Check className="w-3.5 h-3.5 text-yarbo-green mr-1.5 mt-0.5 flex-shrink-0" data-id="element-1280" />
                          <span data-id="element-1281">{b}</span>
                        </li>)}
                    </ul>
                  </button>;
          })}
              <button onClick={() => removeProtection(product.id)} className={`w-full text-center py-3 text-sm font-bold rounded-xl transition-colors ${!protectionPlanId ? 'bg-gray-100 text-gray-900 border-2 border-gray-300' : 'text-gray-500 hover:bg-gray-50 border-2 border-transparent'}`} data-id="element-1282">
                Continue Without Protection
              </button>
            </div>
          </div>)}
      </div>
      <div className="mt-6 flex items-start gap-2 text-xs text-gray-500 bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-1283">
        <Info className="w-4 h-4 flex-shrink-0 mt-0.5" data-id="element-1284" />
        <div data-id="element-1285">
          <p className="mb-1" data-id="element-1286">{YARBOCARE_DISCLAIMER}</p>
          <p data-id="element-1287">{YARBOCARE_PROVIDER_NOTE}</p>
        </div>
      </div>
    </div>;
}
function OrderSummary({
  items,
  itemCount,
  productSubtotal,
  protectionSubtotal,
  subtotal,
  discountAmount,
  shippingEstimate,
  taxEstimate,
  total,
  removeProtection
}: {
  items: ReturnType<typeof useCart>['items'];
  itemCount: number;
  productSubtotal: number;
  protectionSubtotal: number;
  subtotal: number;
  discountAmount: number;
  shippingEstimate: number;
  taxEstimate: number;
  total: number;
  removeProtection: (id: string) => void;
}) {
  return <div className="bg-white border border-gray-200 rounded-2xl p-6 sticky top-24" data-id="element-1288">
      <h2 className="text-lg font-extrabold text-yarbo-dark mb-4" data-id="element-1289">
        Order Summary
      </h2>
      <div className="space-y-4 mb-6 max-h-[40vh] overflow-y-auto hide-scrollbar pr-2" data-id="element-1290">
        {items.map(item => <div key={item.product.id} className="flex gap-3" data-id="element-1291">
            <div className="w-16 h-16 bg-gray-50 rounded-lg border border-gray-200 p-1 flex-shrink-0 relative" data-id="element-1292">
              <img src={item.product.image} alt={item.product.name} className="w-full h-full object-contain" data-id="element-1293" />
              <div className="absolute -top-2 -right-2 w-5 h-5 bg-gray-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center ring-2 ring-white" data-id="element-1294">
                {item.quantity}
              </div>
            </div>
            <div className="flex-1 min-w-0" data-id="element-1295">
              <div className="text-sm font-bold text-yarbo-dark line-clamp-2 leading-tight" data-id="element-1296">
                {item.product.name}
              </div>
              <div className="text-xs text-gray-500 mt-0.5" data-id="element-1297">
                ${item.product.price.toLocaleString()}
              </div>
              {item.protectionPlanId && <div className="mt-1.5 bg-yarbo-yellow/10 border border-yarbo-yellow/30 rounded p-1.5 flex items-start justify-between gap-2" data-id="element-1298">
                  <div className="flex items-start gap-1.5" data-id="element-1299">
                    <ShieldCheck className="w-3.5 h-3.5 text-yarbo-dark flex-shrink-0 mt-0.5" data-id="element-1300" />
                    <div data-id="element-1301">
                      <div className="text-[10px] font-bold text-yarbo-dark leading-tight" data-id="element-1302">
                        {getPlanById(item.protectionPlanId)?.title}
                      </div>
                      <div className="text-[10px] text-gray-600" data-id="element-1303">
                        ${getPlanById(item.protectionPlanId)?.priceMockup}
                      </div>
                    </div>
                  </div>
                  <button onClick={() => removeProtection(item.product.id)} className="text-gray-400 hover:text-red-500 p-0.5" title="Remove protection" data-id="element-1304">
                    <X className="w-3 h-3" data-id="element-1305" />
                  </button>
                </div>}
            </div>
          </div>)}
      </div>

      <div className="space-y-2 text-sm border-t border-gray-200 pt-4 mb-4" data-id="element-1306">
        <div className="flex justify-between text-gray-600" data-id="element-1307">
          <span data-id="element-1308">Products ({itemCount})</span>
          <span data-id="element-1309">${productSubtotal.toLocaleString()}</span>
        </div>
        {protectionSubtotal > 0 && <div className="flex justify-between text-gray-600" data-id="element-1310">
            <span data-id="element-1311">YarboCare Protection</span>
            <span data-id="element-1312">${protectionSubtotal.toLocaleString()}</span>
          </div>}
        {discountAmount > 0 && <div className="flex justify-between text-green-600 font-medium" data-id="element-1313">
            <span data-id="element-1314">Discount</span>
            <span data-id="element-1315">
              -$
              {discountAmount.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })}
            </span>
          </div>}
        <div className="flex justify-between text-gray-600" data-id="element-1316">
          <span data-id="element-1317">Shipping</span>
          <span data-id="element-1318">
            {shippingEstimate === null ? 'Calculated at checkout' : shippingEstimate === 0 ? 'Free' : `$${shippingEstimate.toLocaleString()}`}
          </span>
        </div>
        <div className="flex justify-between text-gray-600" data-id="element-1319">
          <span data-id="element-1320">Estimated Tax</span>
          <span data-id="element-1321">
            {taxEstimate === null ? 'Calculated at checkout' : `$${taxEstimate.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })}`}
          </span>
        </div>
      </div>

      <div className="flex justify-between items-end border-t border-gray-200 pt-4" data-id="element-1322">
        <span className="text-base font-bold text-yarbo-dark" data-id="element-1323">Total</span>
        <span className="text-2xl font-extrabold text-yarbo-dark" data-id="element-1324">
          $
          {total.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })}
        </span>
      </div>
    </div>;
}